import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '@/services/apiService';
import { playClickSound } from '@/utils/soundUtils';
import { shouldOpenInExternalTab } from '@/features/finance/utils/paymentUrlStrategy';

type PendingStatus = 'pending';

interface PendingTransaction {
  id: string;
  paymentUrl: string;
  transactionId?: string;
  platformName: string;
  platformSlug: string;
  amount: number;
  status: PendingStatus;
  createdAt: string;
  provider?: string | null;
}

interface HistoryTransaction {
  id?: number | string;
  transactionId?: string;
  type?: string;
  status?: string;
  paymentUrl?: string | null;
  amount?: number | string;
  createdAt?: string;
  provider?: string | null;
  depositDetails?: Array<{
    gameProvider?: {
      name?: string;
      code?: string;
    };
    amount?: string | number;
    status?: string;
  }>;
}

const USER_ACTION_FETCH_THROTTLE_MS = 30_000;
const PAYMENT_ROUTE_MATCHERS = ['/account/finance/payment-checkout', '/account/wallet/deposit-checkout'];

const normalizePendingStatus = (rawStatus: string): PendingStatus | null => {
  if (rawStatus === 'pending') return 'pending';
  return null;
};

const normalizeProviderSlug = (slug: string) => slug.toLowerCase().replace(/\s+/g, '-');

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'NA';

const formatAmount = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatTimeAgo = (iso: string): string => {
  const value = new Date(iso).getTime();
  if (Number.isNaN(value)) return 'just now';
  const seconds = Math.max(0, Math.floor((Date.now() - value) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const THIRTY_MINUTES_MS = 30 * 60 * 1000;
const PENDING_ITEM_LIMIT = 5;

const mapHistoryToPending = (rows: HistoryTransaction[]): PendingTransaction[] => {
  const cutoff = Date.now() - THIRTY_MINUTES_MS;
  const mapped = rows.flatMap((tx) => {
    const rawStatus = String(tx.status || '').toLowerCase();
    const status = normalizePendingStatus(rawStatus);
    const paymentUrl = tx.paymentUrl || '';
    if (!status || tx.type !== 'deposit' || !paymentUrl || !tx.createdAt) return [];

    const createdAtMs = new Date(tx.createdAt).getTime();
    if (Number.isNaN(createdAtMs) || createdAtMs < cutoff) return [];

    const primaryDetail = tx.depositDetails?.[0];
    const providerName = primaryDetail?.gameProvider?.name || 'Deposit';
    const providerCode = primaryDetail?.gameProvider?.code || providerName;
    const amountRaw = primaryDetail?.amount ?? tx.amount ?? 0;
    const amount = typeof amountRaw === 'number' ? amountRaw : Number(amountRaw) || 0;

    return [{
      id: tx.transactionId || String(tx.id || ''),
      paymentUrl,
      transactionId: tx.transactionId,
      platformName: providerName,
      platformSlug: normalizeProviderSlug(providerCode),
      amount,
      status,
      createdAt: tx.createdAt || new Date().toISOString(),
      provider: tx.provider ?? null,
    }];
  });

  mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return mapped.slice(0, PENDING_ITEM_LIMIT);
};

export default function PendingTransactionFloater() {
  const location = useLocation();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const lastFetchAtRef = useRef<number>(0);
  const isFetchingRef = useRef(false);
  const [pendingTransactions, setPendingTransactions] = useState<PendingTransaction[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [brokenIcons, setBrokenIcons] = useState<Record<string, boolean>>({});
  // Ref mirror so fetchPendingTransactions stays referentially stable across panel toggles.
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;

  const isOnPaymentPage = useMemo(
    () => PAYMENT_ROUTE_MATCHERS.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  const fetchPendingTransactions = useCallback(async () => {
    if (!apiService.getAccessToken()) {
      setPendingTransactions([]);
      return;
    }
    // Never stack requests: if one is in flight (e.g. backend is slow), skip.
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    // Mark the attempt time up-front so the throttle also covers failed/stalled
    // requests. Otherwise a slow backend would let every user action fire a new
    // request, which can trip CDN rate limits.
    lastFetchAtRef.current = Date.now();

    try {
      const now = new Date();
      const from = new Date(Date.now() - THIRTY_MINUTES_MS).toISOString();
      const to = now.toISOString();
      const response = await apiService.get<{ transactions?: HistoryTransaction[] }>(
        `/payment/history?page=1&limit=${PENDING_ITEM_LIMIT}&type=deposit&status=pending&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        true,
        true
      );
      const latestPending = mapHistoryToPending(response.data?.transactions || []);
      setPendingTransactions(latestPending);
      if (latestPending.length === 0 && isPanelOpenRef.current) {
        setIsPanelOpen(false);
      }
    } catch {
      // Keep previous state as-is on network failures.
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  const fetchPendingTransactionsIfAllowed = useCallback(() => {
    const now = Date.now();
    if (now - lastFetchAtRef.current < USER_ACTION_FETCH_THROTTLE_MS) {
      return;
    }
    void fetchPendingTransactions();
  }, [fetchPendingTransactions]);

  useEffect(() => {
    void fetchPendingTransactions();
  }, [fetchPendingTransactions]);

  useEffect(() => {
    if (!isOnPaymentPage) {
      // Returning from checkout should refresh immediately so floater appears without reload.
      void fetchPendingTransactions();
    }
  }, [isOnPaymentPage, fetchPendingTransactions]);

  useEffect(() => {
    const handleUserAction = () => {
      fetchPendingTransactionsIfAllowed();
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchPendingTransactionsIfAllowed();
      }
    };

    window.addEventListener('pointerdown', handleUserAction);
    window.addEventListener('keydown', handleUserAction);
    window.addEventListener('touchstart', handleUserAction);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('pointerdown', handleUserAction);
      window.removeEventListener('keydown', handleUserAction);
      window.removeEventListener('touchstart', handleUserAction);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [fetchPendingTransactionsIfAllowed]);

  useEffect(() => {
    if (!isPanelOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(event.target as Node)) return;
      setIsPanelOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isPanelOpen]);

  useEffect(() => {
    if (isOnPaymentPage && isPanelOpen) {
      setIsPanelOpen(false);
    }
  }, [isOnPaymentPage, isPanelOpen]);

  if (pendingTransactions.length === 0 || isOnPaymentPage) {
    return null;
  }

  const pendingCount = pendingTransactions.length;

  return (
    <>
      <style>{`
        @keyframes pending-floater-spin { to { transform: rotate(360deg); } }
        @keyframes pending-floater-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
        @keyframes pending-floater-slide-in { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      {isPanelOpen && (
        <button
          type="button"
          aria-label="Close pending transactions panel"
          className="fixed inset-0 z-[79] bg-black/35"
          onClick={() => setIsPanelOpen(false)}
        />
      )}

      <div className="fixed left-0 top-1/2 -translate-y-1/2 z-[80]">
        <button
          type="button"
          onClick={() => {
            playClickSound();
            setIsPanelOpen((open) => !open);
          }}
          className="group h-[68px] w-[44px] hover:w-[148px] border-y border-r rounded-r-xl overflow-hidden transition-[width] duration-200 ease-out bg-[#111827]/95 border-white/15 shadow-[0_10px_25px_rgba(0,0,0,0.38)]"
        >
          <span className="flex h-full items-center gap-2 pl-3 pr-4">
            <span className="relative inline-flex">
              <Loader2
                className="h-5 w-5 text-yellow-400"
                style={{ animation: 'pending-floater-spin 1.15s linear infinite' }}
              />
              <span
                className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold leading-[18px] text-black bg-yellow-400"
                style={{ animation: 'pending-floater-pulse 1.2s ease-in-out infinite' }}
              >
                {pendingCount}
              </span>
            </span>
            <span className="max-w-0 opacity-0 whitespace-nowrap text-[13px] text-white/90 transition-all duration-200 ease-out group-hover:max-w-[100px] group-hover:opacity-100">
              {pendingCount} pending
            </span>
          </span>
        </button>
      </div>

      {isPanelOpen && (
        <aside
          ref={panelRef}
          className="fixed left-3 top-1/2 -translate-y-1/2 z-[81] w-[340px] max-w-[calc(100vw-24px)] max-h-[78vh] overflow-hidden rounded-xl border border-white/15 bg-[#0b1220]/95 shadow-[0_20px_45px_rgba(0,0,0,0.48)]"
          style={{ animation: 'pending-floater-slide-in 0.18s ease-out' }}
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div>
              <h3 className="text-sm font-semibold text-white">Pending transactions</h3>
              <p className="text-xs text-white/60">Tap to resume payment</p>
            </div>
            <button
              type="button"
              onClick={() => setIsPanelOpen(false)}
              className="p-1.5 rounded-md text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <div className="max-h-[calc(78vh-62px)] overflow-y-auto">
            {pendingTransactions.map((tx) => {
              const iconPath = `/assets/games/${tx.platformSlug}/icon.png`;
              const showIconFallback = brokenIcons[tx.id];
              return (
                <button
                  key={tx.id}
                  type="button"
                  onClick={() => {
                    playClickSound();
                    setIsPanelOpen(false);
                    // Providers that can't be iframed (CSP frame-ancestors) open in a
                    // new tab. Must happen synchronously inside this click handler,
                    // otherwise mobile popup blockers reject it.
                    if (shouldOpenInExternalTab(tx.provider)) {
                      window.open(tx.paymentUrl, '_blank', 'noopener,noreferrer');
                    }
                    // Resume inside checkout page (waiting screen + status polling for
                    // external-tab providers, embedded iframe for the rest).
                    navigate('/account/finance/deposit');
                  }}
                  className="w-full px-4 py-3 text-left border-b border-white/10 last:border-b-0 hover:bg-white/5 transition-colors"
                >
                  <div className="grid grid-cols-[30px_minmax(0,1fr)_auto] gap-3 items-center">
                    {!showIconFallback ? (
                      <img
                        src={iconPath}
                        alt={tx.platformName}
                        className="w-[30px] h-[30px] rounded-[7px] object-cover bg-white/10"
                        onError={() => setBrokenIcons((prev) => ({ ...prev, [tx.id]: true }))}
                      />
                    ) : (
                      <div className="w-[30px] h-[30px] rounded-[7px] bg-white/10 text-[11px] font-semibold text-white/80 flex items-center justify-center">
                        {getInitials(tx.platformName)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm text-white font-medium">{tx.platformName}</p>
                      <p className="text-xs text-white/60">QR · {formatTimeAgo(tx.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white font-semibold">{formatAmount(tx.amount)}</p>
                      <p
                        className="text-xs inline-flex items-center gap-1"
                        style={{ color: tx.status === 'pending' ? '#facc15' : '#fb923c' }}
                      >
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Pending
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      )}
    </>
  );
}
